import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Trash2, PlusCircle } from "feather-icons-react/build/IconComponents";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { deletePhoneNumber, getAllPhoneNumbers } from "../../core/api/settings";
import Table from "../../core/pagination/datatable";
import AddPhoneNumber from "./addphonenumber";

const PhoneNumbers = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [isAddPhoneNumberVisible, setAddPhoneNumberVisible] = useState(false);
  const MySwal = withReactContent(Swal);
  const selectedClients = useSelector((state) => state.selectedClients);
  const [currentClientId, setCurrentClientId] = useState(null);
  
  const [ loading, setLoading ] = useState( false );
  const loadPhoneNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllPhoneNumbers(selectedClients);
      const flattenedData = transformData(response.data);
      setPhoneNumbers(flattenedData);
      setLoading(false);
    } catch (err) {
      console.error(err);
    } 
  }, [selectedClients]);

  useEffect(() => {
    loadPhoneNumbers();
  }, [selectedClients, loadPhoneNumbers]);

  const showConfirmationAlert = (phoneNumberId) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePhoneNumber(phoneNumberId);
          MySwal.fire({
            title: "Deleted!",
            text: "The phone number has been deleted.",
            icon: "success",
          });
          loadPhoneNumbers();
        } catch (error) {
          console.error("Failed to delete phone number:", error);
        }
      } else {
        MySwal.close();
      }
    });
  };

  const handleAddPhoneNumberClose = () => {
    setAddPhoneNumberVisible(false);
    loadPhoneNumbers();
  };
  const handleAddPhoneNumberClick = () => {
    if (selectedClients.length === 1 && selectedClients[0] !== "all") {
      setAddPhoneNumberVisible(true);
      setCurrentClientId(selectedClients[0]);
    } else if (
      selectedClients.length === 0 ||
      selectedClients.includes("all")
    ) {
      MySwal.fire({
        title: "Please select a client",
        text: "You need to select a specific client to proceed.",
        icon: "warning",
        confirmButtonText: "OK",
      });
    } else if (selectedClients.length > 1) {
      MySwal.fire({
        title: "Please select only one client",
        text: "You can only add a phone number for one client at a time.",
        icon: "warning",
        confirmButtonText: "OK",
      });
    }
  };
  const transformData = (data) =>
    data.flatMap((client) =>
      client.brands.flatMap((brand, brandIndex) =>
        brand.call_centers.flatMap((callCenter, callCenterIndex) =>
          callCenter.phone_numbers.map((phone, phoneIndex) => ({
            key: `${client.client_id}-${brand.brand_id}-${callCenter.call_center_id}-${phone.phone_number}`,
            client_name:
              brandIndex === 0 && callCenterIndex === 0 && phoneIndex === 0
                ? client.client_name
                : "",
            brand_name:
              callCenterIndex === 0 && phoneIndex === 0 ? brand.brand_name : "",
            call_center_name:
              phoneIndex === 0 ? callCenter.call_center_name : "",
            phone_number: phone.phone_number,
            phone_number_id: phone.id,
            status: phone.status,
            clientRowSpan:
              brandIndex === 0 && callCenterIndex === 0 && phoneIndex === 0
                ? client.brands.reduce(
                    (sum, brand) =>
                      sum +
                      brand.call_centers.reduce(
                        (sum, callCenter) =>
                          sum + callCenter.phone_numbers.length,
                        0
                      ),
                    0
                  )
                : 0,
            brandRowSpan:
              callCenterIndex === 0 && phoneIndex === 0
                ? brand.call_centers.reduce(
                    (sum, callCenter) => sum + callCenter.phone_numbers.length,
                    0
                  )
                : 0,
            callCenterRowSpan:
              phoneIndex === 0 ? callCenter.phone_numbers.length : 0,
          }))
        )
      )
    );

  const columns = [
    {
      title: "Brand",
      dataIndex: "brand_name",
      render: (text, record) => {
        if (record.brandRowSpan === 0) return null;
        return <span>{text}</span>;
      },
      onCell: (record) => ({
        rowSpan: record.brandRowSpan,
      }),
    },
    {
      title: "Call Center",
      dataIndex: "call_center_name",
      render: (text, record) => {
        if (record.callCenterRowSpan === 0) return null;
        return <span>{text}</span>;
      },
      onCell: (record) => ({
        rowSpan: record.callCenterRowSpan,
      }),
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
    },
    {
      title: "Status",
      dataIndex: "status",
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              to="#"
              onClick={() => showConfirmationAlert(record.phone_number_id)}
              className="p-2"
              title="Delete"
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
    },
  ];

  const [sidebarWidth, setSidebarWidth] = useState(0);

  return (
    <div className="content">
      <div className="page-header">
        <div className="add-item d-flex">
          <div className="page-title">
            <h4>Phone Numbers List</h4>
            <h6>Manage your phone numbers</h6>
          </div>{" "}
        </div>
        <div className="page-btn">
          <button
            className="btn btn-added"
            onClick={handleAddPhoneNumberClick}
          >
            <PlusCircle className="me-2 iconsize" />
            Add Phone Number
          </button>
        </div>
      </div>
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-responsive">
            <Table
              columns={columns}
              dataSource={phoneNumbers}
              pagination={{ pageSize: 10 }}
              loading={loading}
            />
          </div>
        </div>
      </div>
      <AddPhoneNumber
        isVisible={isAddPhoneNumberVisible}
        onClose={handleAddPhoneNumberClose}
        clientId={currentClientId}
        onResize={setSidebarWidth}
      />
    </div>
  );
};

export default PhoneNumbers;
